import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

let cachedHours = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 min

const formatTime12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  const min = m === '00' ? '' : `:${m}`;
  if (hr === 0) return `12${min}AM`;
  if (hr === 12) return `12${min}PM`;
  return hr > 12 ? `${hr - 12}${min}PM` : `${hr}${min}AM`;
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

export function useBusinessHours() {
  const [hours, setHours] = useState(cachedHours || []);
  const [loading, setLoading] = useState(!cachedHours);

  useEffect(() => {
    if (cachedHours && Date.now() - cacheTimestamp < CACHE_TTL) {
      setHours(cachedHours);
      setLoading(false);
      return;
    }
    fetch(`${API}/api/live-chat/business-hours/public`)
      .then(r => r.json())
      .then(data => {
        const h = data.hours || [];
        cachedHours = h;
        cacheTimestamp = Date.now();
        setHours(h);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { hours, loading };
}

export function formatBusinessHoursSummary(hours) {
  if (!hours || hours.length === 0) return 'Mon-Fri: 9AM - 6PM EST';

  const sorted = [...hours].sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day));
  const openDays = sorted.filter(d => d.is_open);
  if (openDays.length === 0) return 'Currently Closed';

  // Group consecutive days with same hours
  const groups = [];
  let current = null;

  for (const day of openDays) {
    const timeKey = `${day.open_time}-${day.close_time}`;
    if (current && current.timeKey === timeKey && DAY_ORDER.indexOf(day.day) === DAY_ORDER.indexOf(current.endDay) + 1) {
      current.endDay = day.day;
    } else {
      if (current) groups.push(current);
      current = { startDay: day.day, endDay: day.day, timeKey, open: day.open_time, close: day.close_time };
    }
  }
  if (current) groups.push(current);

  return groups.map(g => {
    const range = g.startDay === g.endDay
      ? DAY_SHORT[g.startDay]
      : `${DAY_SHORT[g.startDay]}-${DAY_SHORT[g.endDay]}`;
    return `${range}: ${formatTime12(g.open)} - ${formatTime12(g.close)} EST`;
  }).join(' | ');
}
